import { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

export default function Preloader() {
  const reduce = useReducedMotion();
  const [done, setDone] = useState(false);
  const [pct, setPct] = useState(0);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    let p = 0;
    let loaded = document.readyState === "complete";
    const onLoad = () => {
      loaded = true;
    };
    window.addEventListener("load", onLoad);
    const started = Date.now();

    const iv = setInterval(() => {
      const elapsed = Date.now() - started;
      const ceiling = loaded && elapsed > 1100 ? 100 : 92;
      p += Math.max(0.6, (ceiling - p) * 0.09);
      if (p >= ceiling) p = ceiling;
      setPct(Math.round(p));
      if (p >= 100) {
        clearInterval(iv);
        setTimeout(() => setDone(true), 320);
      }
    }, 28);

    const safety = setTimeout(() => {
      clearInterval(iv);
      setPct(100);
      setDone(true);
    }, 4200);

    return () => {
      clearInterval(iv);
      clearTimeout(safety);
      window.removeEventListener("load", onLoad);
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    if (done) document.body.style.overflow = "";
  }, [done]);

  const R = 54;
  const C = 2 * Math.PI * R;

  return (
    <AnimatePresence>
      {!done && (
        <motion.div
          key="preloader"
          className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-[#050505]"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, filter: reduce ? "blur(0px)" : "blur(6px)" }}
          transition={{ duration: 0.8, ease: EASE }}
          data-testid="preloader"
        >
          <div className="relative flex items-center justify-center" style={{ width: 128, height: 128 }}>
            {/* soft ambient glow */}
            <div
              className="absolute rounded-full"
              style={{
                width: 150,
                height: 150,
                background: "radial-gradient(circle, rgba(120,170,255,0.18), transparent 65%)",
                filter: "blur(6px)",
              }}
            />
            <svg width="128" height="128" viewBox="0 0 128 128" className="relative">
              <circle cx="64" cy="64" r={R} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1.5" />
              <motion.circle
                cx="64"
                cy="64"
                r={R}
                fill="none"
                stroke="rgba(255,255,255,0.9)"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeDasharray={C}
                initial={{ strokeDashoffset: C }}
                animate={{ strokeDashoffset: C - (pct / 100) * C }}
                transition={{ duration: 0.3, ease: "linear" }}
                style={{ filter: "drop-shadow(0 0 6px rgba(150,190,255,0.7))" }}
                transform="rotate(-90 64 64)"
              />
              {!reduce && (
                <motion.g
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2.2, ease: "linear", repeat: Infinity }}
                  style={{ transformOrigin: "64px 64px" }}
                >
                  <circle cx="64" cy={64 - R} r="2.2" fill="#fff" style={{ filter: "drop-shadow(0 0 5px rgba(150,190,255,0.9))" }} />
                </motion.g>
              )}
            </svg>
            <span className="absolute font-display font-bold text-2xl tabular-nums text-white" data-testid="preloader-pct">
              {pct}
            </span>
          </div>

          <motion.p
            className="mt-8 text-[11px] uppercase tracking-[0.5em] text-white/50"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
          >
            Kaya Amah
          </motion.p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
